import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@mui/material";
import api from "../../api";

const STATUS_LABELS = {
  bot: { label: "With assistant", color: "default" },
  queued_for_human: { label: "Passed to our team", color: "warning" },
  assigned: { label: "Team member helping", color: "info" },
  closed: { label: "Closed", color: "default" },
};

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "");

/**
 * Read-only view of a child's own chats with the Study Coach, so parents can see
 * what their child asked and what the assistant told them.
 */
export default function ChildAssistantChats() {
  const [children, setChildren] = useState([]);
  const [childId, setChildId] = useState("");
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("parent/students/names")
      .then((res) => {
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        setChildren(list);
        if (list.length) setChildId(list[0].id);
        else setLoading(false);
      })
      .catch(() => {
        setError("Could not load your students.");
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!childId) return;
    setLoading(true);
    setSelected(null);
    setMessages([]);
    api
      .get(`parent/assistant/student/${childId}/conversations`)
      .then((res) => setConversations(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch((e) => setError(e.response?.data?.message || "Could not load chats."))
      .finally(() => setLoading(false));
  }, [childId]);

  const openConversation = (conv) => {
    setSelected(conv);
    setLoadingMessages(true);
    api
      .get(`parent/assistant/student/${childId}/conversations/${conv.id}/messages`)
      .then((res) => setMessages(res.data?.data?.messages || []))
      .catch((e) => setError(e.response?.data?.message || "Could not load messages."))
      .finally(() => setLoadingMessages(false));
  };

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Study Coach Chats
          </Typography>
          <Typography variant="body2" color="text.secondary">
            See what your child has asked the AI Study Coach and the guidance it gave.
          </Typography>
        </Box>
        {children.length > 1 && (
          <Select size="small" value={childId} onChange={(e) => setChildId(e.target.value)} sx={{ minWidth: 220 }}>
            {children.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.full_name}
              </MenuItem>
            ))}
          </Select>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : !children.length ? (
        <Alert severity="info">Add a student to your account to see their Study Coach chats.</Alert>
      ) : !conversations.length ? (
        <Alert severity="info">No Study Coach chats yet.</Alert>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "340px 1fr" }, gap: 2 }}>
          <Paper variant="outlined" sx={{ maxHeight: { md: "70vh" }, overflowY: "auto" }}>
            <List disablePadding>
              {conversations.map((c) => {
                const status = STATUS_LABELS[c.status] || STATUS_LABELS.bot;
                return (
                  <ListItemButton key={c.id} selected={selected?.id === c.id} onClick={() => openConversation(c)} divider>
                    <ListItemText
                      primary={c.last_question || "Opened the Study Coach"}
                      secondary={
                        <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                          <span>{formatDate(c.updated_at)}</span>
                          {c.status !== "bot" && <Chip size="small" label={status.label} color={status.color} />}
                        </Box>
                      }
                      primaryTypographyProps={{ noWrap: true, fontWeight: 600 }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Paper>

          <Card variant="outlined">
            <CardContent sx={{ maxHeight: { md: "70vh" }, overflowY: "auto", bgcolor: "#f7f7fb" }}>
              {!selected ? (
                <Typography color="text.secondary">Choose a chat to read it.</Typography>
              ) : loadingMessages ? (
                <CircularProgress size={24} />
              ) : (
                messages.map((m) =>
                  m.sender_type === "system" ? (
                    <Typography key={m.id} variant="caption" display="block" textAlign="center" color="text.secondary" my={1}>
                      {m.body}
                    </Typography>
                  ) : (
                    <Box
                      key={m.id}
                      sx={{ display: "flex", justifyContent: m.sender_type === "visitor" ? "flex-end" : "flex-start", mb: 1.25 }}
                    >
                      <Box
                        sx={{
                          maxWidth: "85%",
                          px: 1.5,
                          py: 1,
                          borderRadius: 2,
                          whiteSpace: "pre-wrap",
                          fontSize: 14,
                          ...(m.sender_type === "visitor"
                            ? { bgcolor: "#667eea", color: "#fff" }
                            : { bgcolor: "#fff", border: "1px solid", borderColor: "divider" }),
                        }}
                      >
                        <Typography variant="caption" fontWeight={700} display="block" sx={{ opacity: 0.8 }}>
                          {m.sender_type === "visitor" ? "Your child" : m.sender_type === "admin" ? "Team member" : "Study Coach"}
                          {" · "}
                          {formatDate(m.created_at)}
                        </Typography>
                        {m.body.replace(/\*\*([^*]+)\*\*/g, "$1").replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")}
                      </Box>
                    </Box>
                  )
                )
              )}
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
}
